import { App, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../constants/user-routes"
import { getProgressData, isStepCompleted } from "../../../Utils/onboarding/calculateProgress"
import { isFirstBAInvitedUser } from "../../../Utils/onboarding/isFirstBAInvitedUser"
import { createAccountStepPath } from "../../../Utils/utils"
import { alertsActions } from "../../AlertsPage/redux/alerts.slice"
import BalanceActions from "../../Balance/reducer"
import CreditsActions from "../../CreditsPage/reducer"
import GlobalFiltersActions from "../../GlobalFilters/reducer"
import { invoicesActions } from "../../InvoiceHistoryBlock/redux/invoices.slice"
import { changeNextCreateAccountStep, userSetProp } from "../redux/user.actions"
import { changeBusinessAccount, getBusinessAccounts } from "../redux/user.thunks"

const { Text } = Typography

export const useSwitchBusinessAccount = () => {
  const { notification } = App.useApp()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const { userInfo } = useSelector((state) => state.auth)
  const businessId = userInfo?.current_business_account_id
  const { businessAccountsWithRole } = useSelector((state) => state.user)
  const activeAccount = businessAccountsWithRole.find((account) => account.id === businessId)

  const hasFirstOwnedBA = isFirstBAInvitedUser(businessAccountsWithRole)
  const invitedAndNotFinishedOnboarding =
    userInfo.is_created_by_invitation && !userInfo.new_onboarding_finished
  const hasAdditionalStep = hasFirstOwnedBA && invitedAndNotFinishedOnboarding

  const hasIncompleteBA = businessAccountsWithRole.some((account) => {
    const { done, total } = getProgressData(account.completed_steps)
    return done < total
  })

  const hasBAOnboardingInProgress = hasAdditionalStep || hasIncompleteBA

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content", width: 475 },
    placement: "bottomRight"
  }

  const switchBA = async (newBusinessAccountId) => {
    if (newBusinessAccountId === businessId) return
    const newActiveAccount = businessAccountsWithRole.find(
      (account) => account.id === newBusinessAccountId
    )
    if (!newActiveAccount) return
    const { done, total } = getProgressData(newActiveAccount.completed_steps)
    const isIncomplete = done < total
    const nextStep = done + 1

    // `unwrap()` rethrows on rejection, so a failed switch skips the cleanup and the
    // success notification below — the same shape the old onSuccess/onError pair gave us.
    await dispatch(changeBusinessAccount(newBusinessAccountId)).unwrap()

    dispatch(alertsActions.clearAlertsFilters())
    dispatch(CreditsActions.clearCreditsFilters())
    dispatch(GlobalFiltersActions.clearGlobalFilters())
    dispatch(BalanceActions.clearBalanceFilters())
    dispatch(invoicesActions.clearInvoicesFilters())
    if (isIncomplete || (newActiveAccount?.role?.name === "Owner" && hasAdditionalStep)) {
      dispatch(changeNextCreateAccountStep(nextStep))
      dispatch(userSetProp({ key: "redirectAfterCreateAccount", value: location.pathname }))
      dispatch(getBusinessAccounts())
      navigate(createAccountStepPath[7])
    } else {
      dispatch(changeNextCreateAccountStep(null))

      if (location.pathname?.includes("/getting_started")) {
        navigate(RoutesPaths.dashboard.root)
      }
    }
    notification.success({
      message: t("Business account changed"),
      description: t("You’ve changed your business account successfully."),
      ...notificationConfig
    })
  }

  const goToIncompleteBAOnboarding = () => {
    let incompleteBA = businessAccountsWithRole.find((account) => {
      const { done, total } = getProgressData(account.completed_steps)
      return done < total
    })
    if (!incompleteBA && !hasAdditionalStep) return

    if (!incompleteBA && hasAdditionalStep) {
      incompleteBA = businessAccountsWithRole.find((account) => account?.role?.name === "Owner")
    }
    if (!incompleteBA) return

    if (incompleteBA?.id === businessId) {
      const nextStep = getProgressData(incompleteBA.completed_steps).done + 1
      dispatch(changeNextCreateAccountStep(nextStep))
      navigate(createAccountStepPath[7])
      return
    }

    switchBA(incompleteBA.id)
  }

  return {
    switchBA,
    goToIncompleteBAOnboarding,
    hasBAOnboardingInProgress
  }
}
